import * as React from "react";
import { Tooltip } from "@/components/ui/misc";
import { Spinner } from "@/components/ui/feedback";
import { UserAvatar } from "@/features/user-avatar";
import { useAuth } from "@/lib/auth";
import { findPerson, floorRepOf, personStatus, type PersonRef } from "@/lib/people";
import { useAcdFloor, useUserPages, useUsersById } from "@/lib/queries";
import { atLeast, type AppUser, type FloorRep } from "@/lib/types";
import { cn } from "@/lib/utils";
import { AVAILABILITY_DOT } from "@/softphone/availability";

/**
 * A rep's name on a record that opens their card on hover or focus (ACL-508): their picture, full
 * name, email, and where they are now (online, on a call, busy, on a break, offline). For
 * supervisors and admins, who may read the people and the live floor (`GET /app-users`,
 * `GET /acd/floor`); a rep sees the name as it was. Both are read only while a card is open.
 */
export function PersonName({ className, ...who }: PersonRef & { className?: string }) {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  if (!atLeast(user?.role, "supervisor")) return <span className={className}>{who.name}</span>;
  return (
    <Tooltip variant="card" open={open} onOpenChange={setOpen} content={<PersonCardBody who={who} open={open} />}>
      <button
        type="button"
        className={cn(
          "cursor-default rounded-sm underline decoration-dotted decoration-text-subtle underline-offset-2",
          "hover:decoration-text-muted focus-visible:outline-2 focus-visible:outline-focus",
          className,
        )}
      >
        {who.name}
      </button>
    </Tooltip>
  );
}

function PersonCardBody({ who, open }: { who: PersonRef; open: boolean }) {
  // The person is searched for on the server by the name the record shows, and read by id when the
  // record has one: there is no list of everyone to look in (ACL-491). The floor polls while the
  // card is up.
  const search = useUserPages({ q: who.name }, { enabled: open, pageSize: 20 });
  const byId = useUsersById(who.userId != null ? [who.userId] : [], { enabled: open });
  const floor = useAcdFloor({ enabled: open });
  const person = findPerson([...byId, ...(search.data?.pages.flatMap((p) => p.items) ?? [])], who);
  if (search.isPending && !person) {
    return (
      <div className="flex items-center gap-2 px-3 py-2.5 text-xs text-text-muted">
        <Spinner className="size-3.5" /> {who.name}
      </div>
    );
  }
  if (!person) {
    return (
      <div className="w-60 px-3 py-2.5 text-xs">
        <p className="font-medium text-text">{who.name}</p>
        <p className="mt-0.5 text-text-muted">Not among the people you can see.</p>
      </div>
    );
  }
  return (
    <PersonCard
      person={person}
      rep={floor.data ? floorRepOf(floor.data.reps, person) : floor.isError ? "unknown" : undefined}
    />
  );
}

/** The card itself. `rep` is their floor record, null for none (offline), undefined while the floor
 *  is being read, and "unknown" when it could not be: not knowing is not the same as offline. */
export function PersonCard({ person, rep }: { person: AppUser; rep: FloorRep | null | undefined | "unknown" }) {
  const now = rep === undefined || rep === "unknown" ? null : personStatus(rep);
  return (
    <div className="flex w-72 items-start gap-3 px-3 py-3" aria-label={`${person.display_name ?? person.email}'s card`}>
      <UserAvatar user={person} className="size-10 text-xs" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-text">{person.display_name ?? person.email}</p>
        <p className="truncate text-xs text-text-muted">{person.email}</p>
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-text">
          {now ? (
            <>
              <span aria-hidden className={cn("size-2 shrink-0 rounded-full", AVAILABILITY_DOT[now.tone])} />
              <span className="font-medium">{now.label}</span>
              {now.detail && <span className="truncate text-text-muted">· {now.detail}</span>}
            </>
          ) : (
            <span className="text-text-muted">
              {rep === "unknown" ? "Where they are could not be read" : "Reading where they are…"}
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
